import { useMemo } from 'react'

import { alpha } from '@mui/material/styles'
import { ChartData, ChartOptions } from 'chart.js'
import { format } from 'date-fns'
import { Chart } from 'react-chartjs-2'

import { NETWORK_GROWTH_CHART_HEIGHT } from 'components/reports/Metrics/constants'
import { DATE_DAY_MONTH_SHORT_FORMAT } from 'constants/index'

import { LINE_STYLE } from '../chartConfig'
import { NetworkGrowthPoint } from '../types'

interface Props {
  daily: NetworkGrowthPoint[]
  color: string
  yAxisLabel: string
  suggestedMax?: number
  height?: number
}

export const CumulativeMetricChart = ({
  daily,
  color,
  yAxisLabel,
  suggestedMax,
  height = NETWORK_GROWTH_CHART_HEIGHT,
}: Props) => {
  const { labels, counts } = useMemo(
    () => ({
      labels: daily.map((point) => format(new Date(point.date), DATE_DAY_MONTH_SHORT_FORMAT)),
      counts: daily.map((point) => point.cumulativeCount),
    }),
    [daily],
  )

  const data = useMemo<ChartData<'line', number[], string>>(
    () => ({
      labels,
      datasets: [
        {
          ...LINE_STYLE,
          type: 'line' as const,
          label: yAxisLabel,
          data: counts,
          borderColor: color,
          backgroundColor: alpha(color, 0.12),
          pointBackgroundColor: color,
          fill: true,
        },
      ],
    }),
    [labels, counts, color, yAxisLabel],
  )

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: {
          grid: { display: false },
          ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 7 },
        },
        y: {
          beginAtZero: true,
          suggestedMax,
          title: { display: true, text: yAxisLabel },
          ticks: { precision: 0 },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => `${yAxisLabel}: ${counts[context.dataIndex].toLocaleString()}`,
          },
        },
      },
    }),
    [counts, yAxisLabel, suggestedMax],
  )

  return (
    <div style={{ height, position: 'relative' }}>
      <Chart<'line', number[], string> type='line' data={data} options={options} />
    </div>
  )
}
